import React, { useCallback, useMemo, useState } from 'react';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { BoardType } from '../../api/ApiTypes';
import { TemplateData } from '../../api/endpoints/templates';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { useLoader } from '../../platform/Hook';
import { UseLocationScope } from '../AppLocation';
import { useResolve } from '../Container';
import { ProfileModel } from '../ProfileModel';
import { AccountHeaderBlock } from '../account/AccountHeaderBlock';
import { AccountPage } from '../account/AccountPage';
import { PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { Page } from '../common/Page';
import { UseReturnLocation } from '../common/ReturnLocation';
import { BoardThumb } from '../dashboard/elements/BoardThumb';
import { ContentGrid, Copy, Flexer, Spacer, Title } from '../dashboard/elements/LayoutElements';
import { boardCreateAndOpen } from '../workspace/BoardCommands';
import { Colors, Float } from './../../ui';

const { Item, ItemHeading, ItemBorder, ItemInfo } = ContentGrid;

const TutorialList = () => {

    const api = useResolve(ApiClient);
    const dispatch = useResolve(CommandDispatcher);

    const [working, setWorking] = useState(false);
    const [loading, templates] = useLoader(async () => api.exec(v2.templates.list()), []);
    const tutorials = useMemo(() => (templates ?? []).find(x => x.id === 'tutorials-and-guides')?.items ?? [], [templates]);

    const onSelect = useCallback(async (tmpl: TemplateData) => {
        if (tmpl) {
            if (working) return;
            setWorking(true);
            await dispatch(boardCreateAndOpen, [tmpl.source, tmpl.label, BoardType.ephemeral]);
        }
    }, [working]);

    return <>
        <ContentGrid layout={[28, 28 * .75, 1]}>
            {tutorials.map((tm, i) =>
                <Item key={tm.id} onClick={() => onSelect(tm)}>
                    <ItemHeading>
                        {tm.label}
                    </ItemHeading>
                    <ItemBorder>
                        <BoardThumb info={{ id: tm.source, thumbUrl: tm.thumbUrl }} />
                    </ItemBorder>
                </Item>
            )}
        </ContentGrid>
    </>;
};

export const TutorialsContent = () => {
    const profile = useResolve(ProfileModel);
    return <Page.Content background={Colors.hover}>
        <Flexer padding="4 0" flex="column" center grow>
            <Flexer maxWidth="61">
                <Float inline shadow="sm">
                    <Flexer padding="1 1.5">
                        <Title size="3xl">Hello {profile.shortName}!</Title>
                        <Copy flush>
                            These tutorial boards will guide you through different aspects
                            of the product including taking part in and running meetings.
                            We recommended taking at least the Basic Controls tutorial.
                        </Copy>
                        <Spacer f={1} />
                        <TutorialList />
                    </Flexer>
                </Float>
            </Flexer>
        </Flexer>
    </Page.Content>;
};

export const TutorialsPage = () =>
    <AccountPage>
        <UseReturnLocation href="/tutorials" />
        <UseLocationScope name="tutorials" label="Tutorials" />
        <Page.Header>
            <Page.Title>Tutorial Boards</Page.Title>
            <Page.HeaderPush>
                <PlanStateHeaderBlock />
                <AccountHeaderBlock />
            </Page.HeaderPush>
        </Page.Header>
        <TutorialsContent />
    </AccountPage>;
